import type { ElementType } from 'react';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/element/center-under-pointer';
import { createRoot } from 'react-dom/client';
import type { PlanningEntityType } from 'generated';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';

import type { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

import {
  assignEmploymentEditAppointmentTypeAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from '../../atoms';
import { useIsCardFrozen } from '../../frozenDrop.store';
import { clearSelectionBeforeDrag } from '../dragSelection';

import PopoverWarningDialog from '../../components/PopoverWarningDialog/PopoverWarningDialog';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { ActivityCardPragmatic } from '../ActivityCardPragmatic/ActivityCardPragmatic';
import MultiSelectDragPreview from '../MultiSelectDragPreview/MultiSelectDragPreview';
import type { CopyModeReturn } from '../../hooks/useCopyMode';
import { useIsColumnLoading } from '../ColumnLoadingContext';
import type { SlotDropDelegate } from '../columnSlots';
import { useCardSlotDropTarget } from './DraggableCardPragmatic.hooks';
import { CardContainer, CardListItem } from './DraggableCardPragmatic.styles';

type CardProps = Omit<
  Event,
  | 'droppable'
  | 'category'
  | 'date'
  | 'end'
  | 'status'
  | 'isSubmittable'
  | 'isApprovable'
  | 'isDeletable'
> & {
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
};

interface DraggableCardPragmaticProps extends CardProps {
  component?: ElementType<any>;
  copyMode?: CopyModeReturn;
  selected?: boolean;
  readOnly?: boolean;
  isToPlan?: boolean;
  scheduleStart: string;
  scheduleId?: string;
  /**
   * Total number of cards currently selected in the same list this card
   * belongs to. Used to render the "{count} selected" multi-select drag
   * preview when this card is itself selected and there are siblings.
   */
  selectionCount?: number;
  /**
   * The slots this card's top and bottom halves land in. Supplied by
   * `InnerListPragmatic`, which is the only place that knows what surrounds a
   * card; a card rendered anywhere else (roster cell, "to plan" list) gets
   * neither and registers no drop target.
   */
  dropAbove?: SlotDropDelegate;
  dropBelow?: SlotDropDelegate;
  onHandleSelect?: (
    id: string,
    planningEntityType?: PlanningEntityType,
    employeeId?: string
  ) => void;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  isAlwaysActivityCard?: boolean;
}

function DraggableCardPragmatic(props: DraggableCardPragmaticProps) {
  'use memo';
  const {
    id,
    isRecurring,
    selected,
    scheduleStart,
    scheduleId,
    planningEntityId,
    planningEntityType,
    isToPlan,
    readOnly,
    isAbsent,
    isAlwaysActivityCard,
    selectionCount = 0,
    dropAbove,
    dropBelow,
    onHandleSelect,
    onChangeMousePosition
  } = props;

  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isColumnLoading = useIsColumnLoading();
  // Makes the column's whole height droppable — see the hook.
  const itemRef = useCardSlotDropTarget(
    { above: dropAbove, below: dropBelow },
    isColumnLoading
  );

  // Keep this card faded (no snap-back) while it is the frozen drop source, so
  // the original stays translucent until the API call + refetch settle. Read as
  // a per-card boolean, not as the whole frozen drop: the entire board subscribes
  // to this, and the write happens inside the drop event.
  const isFrozen = useIsCardFrozen(id);

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      isRouteUnassignment,
      scheduleStart: popoverScheduleStart,
      isAssignEmployment,
      isDragAction
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);

  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const hasScheduleStart = !!(popoverScheduleStart && scheduleStart);

  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(scheduleStart).isValid()
    ? dayjs(scheduleStart).format('YYYY-MM-DD')
    : '';

  const isPopOverId = popoverOccurenceId === id;
  const showPopoverDialog: boolean =
    !!isRecurring &&
    (!!isRouteUnassignment ||
      (isPopOverId && !isDragAction) ||
      popoverOccurenceId === 'IS_MULTIPLE' ||
      (isPopOverId &&
        !!isAssignEmployment &&
        hasScheduleStart &&
        popoverScheduleStartDate === currentScheduleStartDate));

  // Stable draggability with NO hover dependency. Used both for the cursor (so
  // it never flickers when `hovered` oscillates — e.g. the tooltip hover loop,
  // or reflows while content loads slowly) and for the drag itself (so a fast
  // mousedown never silently cancels the drag).
  const isDraggable =
    !isAlwaysActivityCard && !isAbsent && !readOnly && !dragDisabled;

  const isDraggableRef = useRef(false);

  const dragDataRef = useRef({
    id,
    planningEntityType,
    planningEntityId,
    scheduleStart,
    scheduleId,
    isToPlan
  });

  // Refs for the multi-select drag preview. Read inside `onGenerateDragPreview`
  // (registered once on mount) so the latest values are available without
  // re-registering `draggable()` on every selection change.
  const selectionCountRef = useRef(selectionCount);
  const isSelectedRef = useRef(!!selected);

  // Keep the refs above pointing at the latest values. Assigned in a layout
  // effect (never during render) so the React Compiler can optimize this
  // component; useLayoutEffect (not useEffect) runs synchronously after commit,
  // so a drag started right after a render never reads a stale `.current`.
  useLayoutEffect(() => {
    isDraggableRef.current = isDraggable;
    dragDataRef.current = {
      id,
      planningEntityType,
      planningEntityId,
      scheduleStart,
      scheduleId,
      isToPlan
    };
    selectionCountRef.current = selectionCount;
    isSelectedRef.current = !!selected;
  });

  // Register the native draggable once on mount.
  // Its canDrag() reads isDraggableRef (updated every render, no hover
  // requirement).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return draggable({
      element: el,
      canDrag: () => isDraggableRef.current,
      getInitialData: () => ({
        ...dragDataRef.current,
        isToPlan:
          dragDataRef.current.isToPlan ??
          dragDataRef.current.planningEntityType ===
            'ORGANIZATIONAL_UNIT_TO_PLAN'
      }),
      onGenerateDragPreview: ({ nativeSetDragImage, source }) => {
        const count = selectionCountRef.current;
        const isMulti = isSelectedRef.current && count > 1;

        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: centerUnderPointer,
          render: ({ container }) => {
            if (!isMulti) {
              const clone = source.element.cloneNode(true) as HTMLElement;
              clone.style.width = `${source.element.offsetWidth}px`;
              container.appendChild(clone);
              return;
            }
            const root = createRoot(container);
            root.render(<MultiSelectDragPreview count={count} />);
            return () => root.unmount();
          }
        });
      },
      onDragStart: () => {
        setIsDragging(true);
        setHovered(false);
      },
      onDrop: () => setIsDragging(false)
    });
  }, []);

  function onSelectClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).classList.contains('MuiBackdrop-root')) {
      return;
    }
    onHandleSelect?.(id);
  }

  return (
    <CardListItem
      component="li"
      ref={itemRef}
      onMouseDown={(e: React.MouseEvent) => {
        clearSelectionBeforeDrag();
        onChangeMousePosition?.({ x: e.clientX, y: e.clientY });
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        !selected && setHovered(false);
      }}
    >
      <PopoverWarningDialog
        open={showPopoverDialog && !isLoading}
        onClose={() => {
          if (isAssignEmployment) {
            setShouldShowPopoverDialog({ id: '' });
            setSelectedContextMenuOption(null);
            return setAssignEmploymentEditAppointmentType(null);
          }
          onClose?.();
          setShouldShowPopoverDialog({ id: '' });
        }}
        onClickAppointment={(editAppointmentType: EditAppointmentType) => {
          if (isAssignEmployment) {
            setShouldShowPopoverDialog({ id: '' });
            return setAssignEmploymentEditAppointmentType(editAppointmentType);
          }
          onHandleAppointmentConfirmation?.(
            editAppointmentType,
            isUnassignment
          );
          setShouldShowPopoverDialog({ id: '' });
        }}
      >
        <CardContainer
          className="draggable-card"
          ref={containerRef}
          onClick={onSelectClick}
          sx={{
            // The card inherits this; it only overrides it for copy mode.
            cursor: isDraggable ? 'grab' : 'default',
            opacity: isDragging || isFrozen ? 0.5 : 1,
            userSelect: 'none'
          }}
        >
          <ActivityCardPragmatic
            {...props}
            selected={!!selected}
            setDragDisabled={setDragDisabled}
            hovered={hovered}
            setHovered={setHovered}
          />
        </CardContainer>
      </PopoverWarningDialog>
    </CardListItem>
  );
}

export default DraggableCardPragmatic;
